import Button from "@/Components/Button/Button";
import Modal from "@/Components/Modal/Modal";
import Select from "@/Components/Select/Select";
import Spinner from "@/Components/Spinner/Spinner";
import TablesMap from "@/Components/Tables/TablesMap";
import { useModalMeta } from "@/shared/atoms/useModal";
import { useSetPosOrderTable } from "@/shared/atoms/usePosOrderTable";
import { posTableDisplaysOptions } from "@/shared/atoms/usePosTableFilter";
import useCommon from "@/shared/hooks/useCommon";
import usePosOrderTable from "@/shared/atoms/usePosOrderTable";
import useTableSessionHook from "@/shared/hooks/useTableSessionHook";
import useOrderSessionsMerge from "@/shared/mutations/orderSessions/useOrderSessionsMerge";
import useActiveOrdersSessionsQuery from "@/shared/queries/orderssessions/useActiveOrdersSessionsQuery";
import useRestaurantMap from "@/shared/queries/restaurantMap/useRestaurantMap";
import useRestaurantMaps from "@/shared/queries/restaurantMap/useRestaurantMaps";
import { asArray } from "@/shared/utils/helpers";
import { RestaurantTable } from "@/shared/utils/interfaces";
import { useEffect, useMemo, useState } from "react";
import useMeasure from "react-use-measure";
import PosTable from "./PosTable";
import usePosSettingsHook from "@/shared/hooks/usePosSettingsHook";

const Tables = ({ active = -1, setTable }) => {
  const [ref, bounds] = useMeasure();

  const [currentTable] = usePosOrderTable();
  const { session } = useTableSessionHook(currentTable?.item_id);
  const { settings } = usePosSettingsHook();
  const [showMap, setShowMap] = useState(settings.is_map_view ?? true);

  useEffect(() => {
    setShowMap(settings.is_map_view);
  }, [settings.is_map_view]);

  const { isPending: isSessionsPending, data: sessions } =
    useActiveOrdersSessionsQuery();
  const t = useCommon();
  const [mapId, setMap] = useState(undefined);
  const { data: maps, isPending: isRestaurantsMapsPending } =
    useRestaurantMaps();
  const chosenMap = useMemo(
    () => asArray(maps)?.find((option) => option.item_id === mapId),
    [mapId, maps]
  );
  const { data: map, isPending } = useRestaurantMap(mapId, mapId !== undefined);
  useEffect(() => {
    if (mapId === undefined && maps?.length > 0) {
      setMap(maps[0].item_id);
    }
  }, [maps, mapId]);

  const filtered = useMemo(() => {
    return (
      map?.tables?.filter(
        (table) =>
          !asArray(session?.current_table_ids)?.includes(table.item_id) &&
          sessions.find(
            (session) =>
              asArray(session?.current_table_ids)?.includes(table.item_id) &&
              session.session_status !== 5 &&
              !session.is_removed
          )
      ) ?? []
    );
  }, [map, sessions]);

  return (
    <div>
      {isRestaurantsMapsPending || isSessionsPending ? (
        <Spinner />
      ) : (
        <div className="mb-8">
          <div className="flex flex-wrap gap-4">
            <div className="basis-[49%] flex-grow md:basis-auto">
              <Select
                menuPlacement="bottom"
                styles={{
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
                menuPortalTarget={document.getElementById("root")}
                options={maps ?? []}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.item_id}
                label={t("chooseUrMap")}
                value={chosenMap}
                onChange={(e) => {
                  setMap(e.item_id);
                }}
              />
            </div>
            <div className="basis-[49%] flex-grow md:basis-auto">
              <Select
                menuPlacement="bottom"
                styles={{
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
                menuPortalTarget={document.getElementById("root")}
                options={posTableDisplaysOptions}
                getOptionLabel={(option) => t(option.label)}
                getOptionValue={(option) => option.value}
                label={t("display")}
                value={posTableDisplaysOptions.find(
                  (option) => option.value === showMap
                )}
                onChange={(e) => {
                  setShowMap(e.value);
                }}
              />
            </div>
          </div>

          {isPending ? (
            <Spinner />
          ) : (
            <div className="mt-8 flex gap-6">
              {filtered.length === 0 ? (
                <div>{t("noTablesAvailable")}</div>
              ) : showMap ? (
                <div ref={ref} className="h-[50rem] w-[67rem]">
                  <TablesMap
                    background_color={map?.background_color}
                    customSelectedTable={[active]}
                    readOnly
                    unavailableTables={sessions.flatMap(
                      (s) => s.current_table_ids
                    )}
                    onTableClick={(table) => {
                      setTable((current) =>
                        current?.item_id === table?.item_id ? null : table
                      );
                    }}
                    width={bounds.width}
                    height={bounds.height}
                    tables={filtered}
                  />
                </div>
              ) : (
                filtered?.map((table) => (
                  <PosTable
                    disable={[currentTable?.item_id]}
                    active={active}
                    mustChooseSession={true}
                    canChooseSession={true}
                    setTable={setTable}
                    table={table}
                  />
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PosMergeSessions = (props) => {
  const setNewTable = useSetPosOrderTable();
  const [{ session_id }] = useModalMeta();
  const mutate = useOrderSessionsMerge();
  const [table, setTable] = useState<RestaurantTable | null>(null);
  const { session } = useTableSessionHook(table?.item_id);
  const t = useCommon();
  const onSave = () => {
    if (table === null) return;
    mutate
      .mutateAsync({
        from_session_id: session_id,
        to_session_id: session.item_id,
      })
      .then(() => {
        setNewTable(table);
        props.onExit();
      });
  };

  return (
    <Modal onExit={() => props.onExit()}>
      <div className="mb-4 text-[3rem] font-black">{t("mergeTables")}</div>
      <Tables active={table?.item_id} setTable={setTable} />
      {table !== null && (
        <Button isPending={mutate.isPending} onClick={onSave}>
          {t("save")}
        </Button>
      )}
    </Modal>
  );
};

export default PosMergeSessions;
